import { type ReactNode } from 'react';

import { type DateRangeOption } from '~/ui/molecules/DateInput';
import { type UserStaticOption } from '~/ui/molecules/SelectServerDriven';

export const UNIVERSAL_FILTER_ALL_ID = 'all' as const;
export const UNIVERSAL_FILTER_PRIMARY_DATE_FIELD =
  '__universalPrimaryDate' as const;
export const UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD =
  '__universalPrimaryPredefinedDateRange' as const;
export const UNIVERSAL_FILTER_SEARCH_FIELD = '__universalSearch' as const;

/**
 * Persistable scalar or array value supported by the saved-filters backend.
 * Complex objects are intentionally excluded for the v1 saved filter shape.
 */
export type FilterValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean>;

export type FilterDraftValue = FilterValue;

/**
 * Persistable saved-filter clause shape.
 * Universal filter drafts use this same simple shape so the suite does not
 * need a separate rich draft model.
 */
export type FilterClause = {
  field: string;
  value?: FilterDraftValue;
};

/**
 * Current saved-filter draft for the selected built-in or backend filter.
 * The primary-row search text is the reserved `UNIVERSAL_FILTER_SEARCH_FIELD`
 * clause of this draft.
 */
export type FilterDraft = readonly FilterClause[];

/**
 * Saved-filter model returned by the backend.
 * The version is used as the optimistic concurrency token for stale updates.
 */
export type BackendSavedFilter = {
  createdOn: string;
  filters: FilterDraft;
  id: string;
  modifiedOn: string;
  name: string;
  type: UniversalSavedFiltersType;
  userId: string;
  version: number;
};

/**
 * Static option for built-in drawer controls.
 */
type UniversalFilterOption = {
  label: string;
  value: FilterValue;
};

// Left to track possible inputs:
//
// /**
// * Drawer inputs the universal suite can render by itself.
// */
//
// type UniversalBuiltInDrawerInput =
//   | 'singleSelect'
//   | 'multiSelect'
//   | 'toggleGroup'
//   | 'booleanToggle'
//   | 'text'
//   | 'companies'
//   | 'costCenters'
//   | 'sites'
//   | 'organizationalUnits'
//   | 'users'
//   | 'customFieldValues';

/**
 * Props passed to page-owned custom drawer field renderers.
 * Use this when a field needs an existing app-specific selector component.
 */
export type UniversalCustomFilterRenderProps = {
  field: string;
  label: string;
  onChange: (value?: FilterDraftValue) => void;
  value?: FilterDraftValue;
};

/**
 * Config for an advanced drawer field rendered by the universal suite.
 * These fields become clauses in the active saved-filter draft.
 */
type UniversalBuiltInDrawerFieldConfigBase = {
  field: string;
  label: string;
  placeholder?: string;
};

type UniversalSelectDrawerFieldConfig =
  UniversalBuiltInDrawerFieldConfigBase & {
    input: 'multiSelect' | 'singleSelect';
    options?: readonly UniversalFilterOption[];
  };

type UniversalToggleDrawerFieldConfig =
  UniversalBuiltInDrawerFieldConfigBase & {
    input: 'booleanToggle' | 'toggleGroup';
    options?: readonly UniversalFilterOption[];
  };

type UniversalTextDrawerFieldConfig = UniversalBuiltInDrawerFieldConfigBase & {
  input: 'text';
  maxLength?: number;
};

type UniversalDateDrawerFieldConfig = UniversalBuiltInDrawerFieldConfigBase & {
  input: 'date';
  /** Field whose date is the latest this field may hold (this field starts the range). */
  maxDateField?: string;
  /** Field whose date is the earliest this field may hold (this field ends the range). */
  minDateField?: string;
};

type UniversalServerDrivenDrawerFieldConfig =
  UniversalBuiltInDrawerFieldConfigBase & {
    input:
      | 'companies'
      | 'costCenters'
      | 'organizationalUnits'
      | 'sites'
      | 'suppliers';
    isMultiSelect?: boolean;
  };

type UniversalUsersDrawerFieldConfig = UniversalBuiltInDrawerFieldConfigBase & {
  input: 'users';
  isMultiSelect?: boolean;
  /**
   * Entries offered ahead of the fetched users. Their ids are stored in the
   * same ID array as user ids.
   */
  staticOptions?: readonly UserStaticOption[];
};

type UniversalCustomFieldValuesDrawerFieldConfig =
  UniversalBuiltInDrawerFieldConfigBase & {
    customFieldId: string;
    input: 'customFieldValues';
    isMultiSelect?: boolean;
  };

export type UniversalBuiltInDrawerFieldConfig =
  | UniversalDateDrawerFieldConfig
  | UniversalSelectDrawerFieldConfig
  | UniversalServerDrivenDrawerFieldConfig
  | UniversalTextDrawerFieldConfig
  | UniversalToggleDrawerFieldConfig
  | UniversalCustomFieldValuesDrawerFieldConfig
  | UniversalUsersDrawerFieldConfig;

/** Values of a drawer row's owned clause keys present in the draft. */
export type UniversalFilterClauseValues = Readonly<
  Record<string, FilterDraftValue | undefined>
>;

/**
 * Props passed to page-owned renderers of a row that owns several clause keys.
 * `onChange` stores the row's one clause, under whichever owned key it names.
 */
export type UniversalMultiKeyCustomFilterRenderProps = {
  onChange: (clause: FilterClause) => void;
  values: UniversalFilterClauseValues;
};

/**
 * Config for an advanced drawer field rendered by the consuming page.
 * The universal suite still owns the clause value through onChange.
 */
type UniversalSingleKeyCustomDrawerFieldConfig = {
  field: string;
  fields?: undefined;
  input: 'custom';
  label: string;
  render: (props: UniversalCustomFilterRenderProps) => ReactNode;
};

/**
 * Config for a page-rendered row that owns several clause keys. `field` is only
 * the row id and is never stored as a clause; `fields` lists the owned clause
 * keys, with the key a newly added row starts under first.
 */
export type UniversalMultiKeyCustomDrawerFieldConfig = {
  field: string;
  fields: readonly [string, ...string[]];
  input: 'custom';
  label: string;
  render: (props: UniversalMultiKeyCustomFilterRenderProps) => ReactNode;
};

export type UniversalCustomDrawerFieldConfig =
  | UniversalSingleKeyCustomDrawerFieldConfig
  | UniversalMultiKeyCustomDrawerFieldConfig;

export type UniversalDrawerFieldConfig =
  | UniversalBuiltInDrawerFieldConfig
  | UniversalCustomDrawerFieldConfig;

/**
 * Optional always-visible date/date-range filter for the primary row.
 * When configured, the universal primary date fields become part of the active
 * saved-filter draft and the consuming page maps them to its API payload.
 */
export type UniversalDateFieldConfig = {
  defaultPredefinedRange?: DateRangeOption;
  label: string;
};

/** Optional text search for the primary row. */
export type UniversalSearchConfig = {
  placeholder?: string;
};

/**
 * Page-level contract for wiring the universal filters suite into one table/list page.
 *
 * The universal module owns generic filter UI/state, while the consuming page
 * owns the meaning of fields and how they map to that page's query/body payload.
 */
export type UniversalFiltersPageConfig<TPayload> = {
  /**
   * Runtime custom-field keys available on this page.
   *
   * Saved filters use the custom-field key as the backend field, which starts
   * with the custom field UUID. Rendering code can resolve labels, input types,
   * and options from the custom-fields query.
   */
  availableCustomFieldKeys?: readonly string[];
  /**
   * Default filter clauses applied to the built-in All filter baseline.
   * These are pre-populated in the drawer but can be changed or cleared by the user.
   */
  defaultFilters?: FilterDraft;
  /**
   * Optional primary date/date-range field. PrimaryFiltersRow can use its
   * existence to decide whether to render the date control.
   */
  dateField?: UniversalDateFieldConfig;
  /**
   * Optional advanced drawer fields. When absent or empty, pages can hide the
   * drawer trigger and omit FiltersDrawer entirely.
   */
  drawerFields?: readonly UniversalDrawerFieldConfig[];
  /**
   * Converts the current generic filter state into the typed payload expected
   * by the consuming page's table query hook.
   *
   * Page-specific field names, casing conversion, date splitting, and URL/body
   * payload shape decisions belong here.
   */
  mapToPayload: (input: {
    filters: FilterDraft;
    search: string;
  }) => Partial<TPayload>;
  /**
   * Enables backend-backed saved filters for this page.
   * Pages can omit this and still use the default built-in All filter with
   * local filtering.
   */
  savedFilters?: {
    enabled: boolean;
  };
  /**
   * Optional search field. PrimaryFiltersRow can use its existence to
   * decide whether to render the search input.
   */
  search?: UniversalSearchConfig;
  /**
   * Stable identifier for the in-memory filter-state cache.
   *
   * Defaults to `type`. Set this explicitly when multiple lists share the same
   * saved-filter `type` but must NOT share restored draft state (for example,
   * incoming vs outgoing invoices both use type 'invoices').
   */
  persistenceKey?: string;
  /**
   * Saved-filter backend scope, for example "deliveries", "scans", or "invoices".
   */
  type: UniversalSavedFiltersType;
};

/**
 * Saved-filter backend scope, for example "deliveries", "scans", or "invoices".
 */

export const UNIVERSAL_FILTER_TYPES = {
  contracts: 'contracts',
  deliveries: 'deliveries',
  invoices: 'invoices',
  invoicesV2: 'invoices-v2',
  ledgerAccounts: 'ledger-accounts',
  scans: 'scans',
  suppliers: 'suppliers',
  taxCodes: 'tax-codes',
} as const;

export type UniversalSavedFiltersType =
  (typeof UNIVERSAL_FILTER_TYPES)[keyof typeof UNIVERSAL_FILTER_TYPES];
